import { useCallback, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

/**
 * 「开始揽收 + 部分揽收」灰度开关（2026-09 需求，首批仅放 LAXS TEMU HUB=65）。
 *
 *   startPartialEnabled(hub) = wholeSwitch || hubIds.contains(hub)
 *
 * 单一源：api-server 自己的 `common_config` key `pickup.start.partial.gray.switch`，
 * 经 GraphQL `getPickupStartPartialGraySwitch` 暴露。**直查库无缓存，改配置即时生效。**
 *
 * 运营台据此决定是否渲染：实际入库包裹明细链接、揽收任务列表「实际出发时间」列、
 * 揽收进度的部分揽收监控抽屉。
 *
 * 与 usePickupSiteV2Gray / usePickupTrackingScanGray / useAddressAbnormalGray 是**第四份独立名单**，
 * 理由同那几处：挂在一起就无法独立放量与回滚。
 *
 * 司机端不读这个接口——那边由后端随任务查询下发布尔值，避免「任务出来了但开关还在飞行」的中间态。
 */
export interface PickupStartPartialGraySwitch {
  wholeSwitch: boolean;
  hubIds: string[];
}

export const GET_PICKUP_START_PARTIAL_GRAY_SWITCH = gql`
  query GetPickupStartPartialGraySwitch {
    getPickupStartPartialGraySwitch {
      wholeSwitch
      hubIds
    }
  }
`;

interface PickupStartPartialGrayResponse {
  getPickupStartPartialGraySwitch?: {
    wholeSwitch: boolean;
    // 后端 hubIds 为 Long，GraphQL 序列化为字符串
    hubIds: Array<string | number> | null;
  } | null;
}

export function usePickupStartPartialGray() {
  const { data, loading } = useQuery<PickupStartPartialGrayResponse>(
    GET_PICKUP_START_PARTIAL_GRAY_SWITCH,
    // 不能用 cache-first：后端每次实时读 common_config，前端却会把结果缓存到页面刷新为止。
    // 放量/回滚那一刻两边必然不一致——运营停在旧页面上，按钮还在但后端已经关了，
    // 点下去只会拿到一个看不出原因的拒绝。这个查询只有两个字段，实时性远比省一次请求重要。
    // cache-and-network 先用缓存渲染、同时后台刷新，不会闪空白。
    { fetchPolicy: 'cache-and-network' },
  );
  const cfg = data?.getPickupStartPartialGraySwitch;
  const wholeSwitch = cfg?.wholeSwitch ?? false;
  const hubIds = useMemo(() => (cfg?.hubIds ?? []).map(String), [cfg]);

  const startPartialEnabled = useCallback(
    (hubId?: string | number | null): boolean => {
      // 失败关闭：加载中 / 无 hub 上下文一律未命中，避免「任一 hub 灰度即全量」
      if (hubId === null || hubId === undefined || hubId === '') return false;
      return wholeSwitch || hubIds.includes(String(hubId));
    },
    [wholeSwitch, hubIds],
  );

  return { wholeSwitch, hubIds, startPartialEnabled, loading };
}
